import { useEffect, useMemo, useState } from "react";

import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";

export interface LatLng {
  lat: number;
  lng: number;
}

/** A collection to plot: its position, visiting order and truck styling. */
export interface TripMapMarker {
  id: string;
  lat: number;
  lng: number;
  order: number;
  colour: string;
  isVan: boolean;
  is4x4: boolean;
  truckName: string;
  label: string;
  postcode: string;
  tripId: string;
  /** A lead plotted outside the day's routes because it isn't on a trip yet. */
  isUnscheduled?: boolean;
}

/** Road geometry per trip ([lat, lng] positions), keyed by tripId. */
export type RouteGeometry = Record<string, number[][]>;

interface TripDayMapProps {
  markers: TripMapMarker[];
  /**
   * When set, the map previews this proposed order (solid route + numbered pins)
   * and draws the current order underneath as a faded dashed "ghost" route.
   */
  previewMarkers?: TripMapMarker[];
  /** Mapbox road geometry for the previewed (solid) route; falls back to straight legs. */
  previewGeometry?: RouteGeometry;
  depot: LatLng;
  /**
   * Markers plotted regardless of the trip filter or preview state - used for the
   * selected lead when it isn't one of the day's routed stops (e.g. an unscheduled
   * lead picked from the panel).
   */
  extraMarkers?: TripMapMarker[];
  selectedLeadId?: string;
  onMarkerClick: (id: string) => void;
}

// A coloured circular pin showing the stop's order number, with a van/4x4 badge.
function buildPinIcon(marker: TripMapMarker, isSelected: boolean) {
  const size = 34;
  const badge = marker.isVan ? "🚐" : marker.is4x4 ? "🚙" : "";
  const border = isSelected ? "#111827" : "#ffffff";
  // Unscheduled leads have no visiting order, so they get an amber dashed pin
  // with a plain centre instead of a numbered, trip-coloured one.
  const background = marker.isUnscheduled ? "#d97706" : marker.colour;
  const borderStyle = marker.isUnscheduled ? "dashed" : "solid";
  const inner = marker.isUnscheduled
    ? `<div style="width:10px;height:10px;border-radius:9999px;background:#ffffff;"></div>`
    : `${marker.order}`;

  const badgeHtml = badge
    ? `<div style="position:absolute;top:-6px;right:-6px;background:#ffffff;border-radius:9999px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;font-size:10px;line-height:1;box-shadow:0 1px 2px rgba(0,0,0,0.35);">${badge}</div>`
    : "";

  return L.divIcon({
    className: "trip-day-pin",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
    html: `
      <div style="position:relative;width:${size}px;height:${size}px;">
        <div style="width:${size}px;height:${size}px;border-radius:9999px;background:${background};border:2px ${borderStyle} ${border};box-shadow:0 1px 3px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;color:#ffffff;font-weight:700;font-size:13px;font-family:sans-serif;">${inner}</div>
        ${badgeHtml}
      </div>`,
  });
}

// A small anchor for the yard/depot so the out-and-back route legs make sense.
function buildDepotIcon() {
  const size = 26;
  return L.divIcon({
    className: "trip-day-depot",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:6px;background:#111827;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;font-size:13px;line-height:1;">🏭</div>`,
  });
}

interface TripRoute {
  tripId: string;
  colour: string;
  positions: [number, number][];
}

// Groups markers into a route per trip: depot → stops (in order) → depot.
function buildRoutes(markers: TripMapMarker[], depot: LatLng): TripRoute[] {
  const byTrip = new Map<string, TripMapMarker[]>();
  markers.forEach((marker) => {
    if (!marker.tripId) return;
    const group = byTrip.get(marker.tripId) ?? [];
    group.push(marker);
    byTrip.set(marker.tripId, group);
  });

  const routes: TripRoute[] = [];
  byTrip.forEach((tripMarkers, tripId) => {
    const ordered = tripMarkers.slice().sort((a, b) => a.order - b.order);
    routes.push({
      tripId,
      colour: ordered[0]?.colour ?? "#6b7280",
      positions: [
        [depot.lat, depot.lng],
        ...ordered.map((m) => [m.lat, m.lng] as [number, number]),
        [depot.lat, depot.lng],
      ],
    });
  });

  return routes;
}

// Keeps the viewport framed on the current day's stops.
function FitBounds({ markers, depot }: { markers: TripMapMarker[]; depot: LatLng }) {
  const map = useMap();

  useEffect(() => {
    if (markers.length === 0) {
      map.setView([depot.lat, depot.lng], 8, { animate: false });
      return;
    }

    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng] as [number, number]));
    bounds.extend([depot.lat, depot.lng]);
    // animate:false avoids a Leaflet crash if the panel collapses / remounts mid-zoom.
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12, animate: false });
  }, [markers, depot, map]);

  return null;
}

// Prefers the real road geometry for a trip, falling back to its straight legs.
function positionsForRoute(route: TripRoute, geometry?: RouteGeometry): number[][] {
  const roadPositions = geometry?.[route.tripId];
  return roadPositions && roadPositions.length > 1 ? roadPositions : route.positions;
}

/**
 * Leaflet map of a day's collections. Draws numbered pins per stop and, when a
 * proposed plan is being previewed, the optimised route (solid, using Mapbox road
 * geometry) over the current order shown as a faded dashed "ghost" so the change
 * is easy to compare. Ported from the Adam dashboard and decoupled from the lead
 * model so it only depends on plain marker data.
 */
export default function TripDayMap({
  markers,
  previewMarkers,
  previewGeometry,
  depot,
  extraMarkers,
  selectedLeadId,
  onMarkerClick,
}: TripDayMapProps) {
  const [isReady, setIsReady] = useState(false);

  const isPreview = previewMarkers != null;
  // Pins/route follow the proposed order when previewing, otherwise the current one.
  const pins = isPreview ? previewMarkers! : markers;
  const routes = useMemo(() => buildRoutes(pins, depot), [pins, depot]);

  // The current order shown faded underneath so the change is easy to compare.
  // Limited to the trips being previewed so single-trip previews stay focused.
  const ghostRoutes = useMemo(() => {
    if (!isPreview) return [];
    const previewTripIds = new Set(pins.map((p) => p.tripId));
    const relevant = markers.filter((m) => previewTripIds.has(m.tripId));
    return buildRoutes(relevant, depot);
  }, [isPreview, pins, markers, depot]);

  const extras = useMemo(() => extraMarkers ?? [], [extraMarkers]);

  const boundsMarkers = useMemo(
    () => (isPreview ? [...pins, ...markers, ...extras] : [...markers, ...extras]),
    [isPreview, pins, markers, extras]
  );

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) return null;

  return (
    <div className="h-[45vh] w-full z-10">
      <MapContainer
        className="h-full z-10"
        center={[depot.lat, depot.lng]}
        zoom={8}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          referrerPolicy="origin"
        />
        <FitBounds markers={boundsMarkers} depot={depot} />

        {ghostRoutes.map((route) => (
          <Polyline
            key={`ghost-${route.tripId}`}
            positions={positionsForRoute(route) as [number, number][]}
            pathOptions={{ color: "#9ca3af", weight: 2, opacity: 0.5, dashArray: "6 6" }}
          />
        ))}

        {isPreview &&
          routes.map((route) => (
            <Polyline
              key={route.tripId}
              positions={positionsForRoute(route, previewGeometry) as [number, number][]}
              pathOptions={{ color: route.colour, weight: 3, opacity: 0.9 }}
            />
          ))}

        {pins.length > 0 && (
          <Marker position={[depot.lat, depot.lng]} icon={buildDepotIcon()}>
            <Tooltip direction="top" offset={[0, -14]}>
              <div className="text-xs font-semibold">Site</div>
            </Tooltip>
          </Marker>
        )}

        {pins.map((marker) => (
          <Marker
            key={marker.id}
            position={[marker.lat, marker.lng]}
            icon={buildPinIcon(marker, marker.id === selectedLeadId)}
            eventHandlers={{ click: () => onMarkerClick(marker.id) }}
          >
            <Tooltip direction="top" offset={[0, -18]}>
              <div className="text-xs">
                <div className="font-semibold">
                  #{marker.order} · {marker.label}
                </div>
                <div>{marker.postcode}</div>
                <div className="text-gray-500">{marker.truckName}</div>
              </div>
            </Tooltip>
          </Marker>
        ))}
        {extras.map((marker) => (
          <Marker
            key={`extra-${marker.id}`}
            position={[marker.lat, marker.lng]}
            icon={buildPinIcon(marker, marker.id === selectedLeadId)}
            eventHandlers={{ click: () => onMarkerClick(marker.id) }}
          >
            <Tooltip direction="top" offset={[0, -18]}>
              <div className="text-xs">
                <div className="font-semibold">{marker.label}</div>
                <div>{marker.postcode}</div>
                <div className="text-gray-500">
                  {marker.isUnscheduled ? "Unscheduled" : marker.truckName}
                </div>
              </div>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
